import React, { useId, useRef } from 'react';
import { Box, Divider, Drawer, IconButton, Toolbar, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { StaffPerformanceRecord } from '../types';
import { formatDisplayName } from '../utils/metrics';
import { clientUpdatesSummary } from '../utils/successUpdates';
import { ClientUpdatesList } from './ClientUpdatesList';

export interface ClientUpdatesDrawerProps {
  /** The coach whose clients to list; `null` closes the drawer. */
  record: StaffPerformanceRecord | null;
  onClose: () => void;
}

/** Right-hand panel listing one coach's overdue clients and those still awaiting a first update. */
export const ClientUpdatesDrawer: React.FC<ClientUpdatesDrawerProps> = ({ record, onClose }) => {
  const titleId = useId();
  // Keep showing the last coach while the panel slides shut, rather than emptying it mid-animation.
  const shown = useRef<StaffPerformanceRecord | null>(record);
  if (record) shown.current = record;
  const current = record ?? shown.current;

  const updates = current?.success_updates;

  return (
    <Drawer
      anchor="right"
      open={record !== null}
      onClose={onClose}
      PaperProps={{ role: 'dialog', 'aria-labelledby': titleId, sx: { width: { xs: '100%', sm: 420 }, maxWidth: '100%' } }}
    >
      {current ? (
        <>
          {/* The app bar sits above drawers; this spacer, sized like it, keeps the header and close button in view. */}
          <Toolbar aria-hidden sx={{ flexShrink: 0 }} />
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, px: 2.5, pt: 2.5, pb: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography id={titleId} component="h2" variant="h6" sx={{ fontWeight: 700 }}>
                {formatDisplayName(current)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {clientUpdatesSummary(updates?.counts)}
              </Typography>
            </Box>
            <IconButton aria-label="Close" onClick={onClose} edge="end" sx={{ mt: -0.5 }}>
              <Close />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 1 }}>
            <ClientUpdatesList updates={updates} />
          </Box>
        </>
      ) : null}
    </Drawer>
  );
};
